<template>
  <div class="relative">
    <div class="overflow-x-auto overflow-y-hidden data-grid relative">
      <div class="header-row contents">
        <div v-if="choosable" class="checkbox-container" @click="checkAll">
          <div class="checkbox" :class="{ active: isAllChecked }" />
        </div>
        <div v-if="pinnable" />
        <div
          v-for="column in preparedColumns"
          :key="column.key"
          :role="column.sortable ? 'button' : ''"
          tabindex="0"
          class="flex gap-x-2 justify-between p-2.5 text-table-header whitespace-nowrap"
          :class="{ sortable: column.sortable }"
          @click="column.sortable && changeSort(column)"
        >
          {{ column.title }}
          <SortIcon
            v-if="column.sortable"
            :direction="isSortedBy(column) ? sortDirectionOfColumn(column)?.toLowerCase() : undefined"
          />
        </div>
        <div v-if="!!actions" class="text-table-header action-cell">
          {{ $t('actions') }}
        </div>
      </div>
      <template v-if="busy" class="full-row">
        <fake-rows
          :columns="preparedColumns"
          :actions="!!actions"
          :choosable="choosable"
          :pinnable="pinnable"
        />
        <div class="spinner">
          <Spinner />
        </div>
      </template>
      <!-- Start "No results" row -->
      <template v-else-if="preparedItems.length === 0">
        <slot name="empty">
          <fake-rows
            :columns="preparedColumns"
            :actions="!!actions"
            :choosable="choosable"
            :pinnable="pinnable"
          />
          <Alert
            variant="info"
            :title="$t('table.noDataTitle')"
            :sub-title="$t('table.noDataSubtitle')"
            class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        </slot>
      </template>
      <!-- End "No results" row -->

      <!-- Start Item row -->
      <template v-else-if="preparedItems.length > 0">
        <div
          v-for="item in preparedItems"
          :key="'row-' + item.id"
          class="data-row contents"
          :class="{ 'linked-row': !!rowLink || !!rowClick }"
        >
          <div v-if="choosable" class="checkbox-container" @click="checkItem(item.id)">
            <div class="checkbox" :class="{ active: isChecked(item.id) }" />
          </div>
          <div v-if="pinnable">
            <PinIcon v-if="pinnedFn(item)" class="pin" />
          </div>
          <div
            v-for="column in preparedColumns"
            :key="item.id + '_' + column.key"
            class="data-cell"
            :class="column.class"
          >
            <slot :name="column.key" v-bind="{ item, value: getCell(column, item), column }">
              <router-link
                v-if="column.to && unwrapValue(column.to, item, column)"
                class="column-link"
                :to="unwrapValue(column.to, item, column)"
              >
                <template v-if="column.isHtml">
                  <span v-html="getCell(column, item)" />
                </template>
                <template v-else>
                  {{ getCell(column, item) }}
                </template>
              </router-link>
              <a
                v-else-if="column.link"
                class="column-link"
                :href="unwrapValue(column.link, item, column)"
                :target="column.linkTarget"
                :rel="column.linkTarget === '_blank' ? 'noopener noreferrer' : undefined"
              >
                <template v-if="column.isHtml">
                  <span v-html="getCell(column, item)" />
                </template>
                <template v-else>
                  {{ getCell(column, item) }}
                </template>
              </a>
              <router-link
                v-else-if="rowLink && unwrapValue(rowLink, item, column) !== undefined"
                class="row-link"
                :to="unwrapValue(rowLink, item, column)"
              >
                <template v-if="column.isHtml">
                  <span v-html="getCell(column, item)" />
                </template>
                <template v-else>
                  {{ getCell(column, item) }}
                </template>
              </router-link>
              <span
                v-else-if="rowClick && typeof rowClick === 'function'"
                class="row-click"
                @click="rowClick?.(item, column)"
              >
                <template v-if="column.isHtml">
                  <span v-html="getCell(column, item)" />
                </template>
                <template v-else>
                  {{ getCell(column, item) }}
                </template>
              </span>
              <span v-else>
                <template v-if="column.isHtml">
                  <span v-html="getCell(column, item)" />
                </template>
                <template v-else>
                  {{ getCell(column, item) }}
                </template>
              </span>
            </slot>
          </div>
          <div v-if="actions" class="data-cell action-cell">
            <Popper
              v-if="(activeActions?.(item)?.length ?? 0) > 0"
              close-on-click-outside
              close-on-esc
              placement="bottom-end"
              :teleport-to="`#${actionId}`"
            >
              <template #default="{ toggle }">
                <button variant="primary" class="py-2" @click="toggle">
                  <Points class="w-8" />
                </button>
              </template>
              <template #content="{ close }">
                <Menu>
                  <MenuItem
                    v-for="action in activeActions(item)"
                    :key="action.title"
                    @click="
                      () => {
                        action.action(item);
                        close();
                      }
                    "
                  >
                    {{ action.title }}
                  </MenuItem>
                </Menu>
              </template>
            </Popper>
          </div>
        </div>
      </template>
      <!-- End Item row -->
    </div>

    <div :id="actionId" />
  </div>
</template>

<script setup lang="ts">
import {
  computed, unref, type FunctionalComponent, ref, watch,
} from 'vue';
import { useVModel, type VueInstance } from '@vueuse/core';
import type { RouterLinkProps } from 'vue-router';
import { RouterLink } from 'vue-router';
import Popper from '@components/utils/Popper.vue';
import Menu from '@components/utils/menu/Menu.vue';
import MenuItem from '@components/utils/menu/MenuItem.vue';
import Points from '@images/points.svg?component';
import { v4 as uuidv4 } from 'uuid';
import SortIcon from '@/components/utils/table/SortIcon.vue';
import Spinner from '@/components/utils/Spinner.vue';
import Alert from '@/components/utils/Alert.vue';
import PinIcon from '@/assets/images/pin.svg?component';
import FakeRows from './FakeRows.vue';

export interface Action {
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: (item: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  condition?: (item: any) => boolean;
}

export interface Column {
  key: string;
  title?: string;
  sortable?: boolean;
  sortKey?: string | string[];
  visible?: boolean;
  class?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  to?: RouterLinkProps['to'] | ((item: any, column: Column) => RouterLinkProps['to'] | undefined);
  link?: string | ((item: any, column: Column) => string);
  linkTarget?: '_blank' | '_self' | '_parent' | '_top';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  computed?: (value: any) => VueInstance | FunctionalComponent | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatter?: (value: any) => string;
  comparator?: <T>(a: T, b: T) => -1 | 0 | 1;
  [key: string]: unknown; // Allow additional parameters that don't concern us
  isHtml?: boolean;
}

export interface Props<T extends { id: string }> {
  busy?: boolean;
  columns?: Column[];
  visibleColumns?: Record<string, boolean>;
  items?: T[];
  rowLink?: RouterLinkProps['to'] | ((item: any, column: Column) => RouterLinkProps['to']);
  rowClick?: (item: any, column: Column) => void;
  sortDirection?: 'ASC' | 'DESC' | Array<'ASC' | 'DESC'>;
  sortBy?: string | string[];
  actions?: Action[];
  choosable?: boolean;
  checkedIdsList?: string[];
  disableFrontendSorting?: boolean;
  pinnable?: boolean;
  pinnedFn?: (item: T) => boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const props = withDefaults(defineProps<Props<any>>(), {
  busy: false,
  // eslint-disable-next-line vue/require-valid-default-prop
  sortDirection: () => 'ASC',
  items: () => [],
  columns: () => [],
  choosable: false,
  disableFrontendSorting: false,
  pinnable: false,
  pinnedFn: (item: any) => item?.pinned ?? false,
});

const componentId = uuidv4();
const actionId = `actions-target-${componentId}`;

const emit = defineEmits(['update:sortBy', 'update:sortDirection', 'update:checkedIdsList']);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const activeActions = (item: any): Action[] | undefined =>
  props.actions?.filter((action) => action.condition?.(item) ?? true);

const internalSortBy = useVModel(props, 'sortBy', emit, { passive: true });
const internalSortDirection = useVModel(props, 'sortDirection', emit, { passive: true });

const unwrapValue = <V extends string | object, T>(
  value: V | ((item: T, column: Column) => V),
  item: T,
  column: Column,
): V => (typeof value === 'function' ? value(item, column) : value);

const isSortedBy = (column: Column) => {
  const sort = unref(internalSortBy);
  const key = column.sortKey ?? column.key;

  return Array.isArray(key) && Array.isArray(sort)
    ? key.every((part, i) => sort[i] === part)
    : Array.isArray(key) === Array.isArray(sort) && key === sort;
};

const sortDirectionOfColumn = (column: Column) => {
  const key = column.sortKey ?? column.key;
  const sortDirection = unref(internalSortDirection);
  if (Array.isArray(key)) {
    // May change in the future if multi-column sort is to be implemented
    return Array.isArray(sortDirection) ? sortDirection[key.indexOf(column.key)] : sortDirection;
  }
  return Array.isArray(sortDirection) ? sortDirection[0] : sortDirection;
};

const changeSort = (column: Column) => {
  const sortDirection = unref(internalSortDirection);
  const key = column.sortKey ?? column.key;

  const isSameKey = isSortedBy(column);

  if (!isSameKey) {
    internalSortBy.value = key;
    internalSortDirection.value = 'ASC';
    return;
  }

  if (sortDirection === 'DESC') {
    internalSortBy.value = undefined;
    internalSortDirection.value = 'ASC';
    return;
  }
  internalSortDirection.value = 'DESC';
};

const getObjectProperty = <T extends Record<string, T>>(o: T, p: string) => {
  let s = p.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  s = s.replace(/^\./, ''); // strip a leading dot
  const a = s.split('.');
  let result = o;
  for (let i = 0, n = a.length; i < n; i += 1) {
    const k = a[i];
    if (typeof result === 'object' && result && k in result) {
      result = result[k];
    } else {
      return undefined;
    }
  }
  return result;
};

const getColumnData = <T extends Record<string, T>>(col: Column, item: T) => {
  if (col.computed) return col.computed(item);
  if (col.key) return getObjectProperty(item, col.key);
  return null;
};

const getCell = <T extends Record<string, T>>(col: Column, item: T) => {
  const data = getColumnData(col, item);
  return col.formatter ? col.formatter(data) : data;
};

const preparedColumns = computed(() =>
  props.columns
    .map((col) => ({ ...col, sortable: 'sortable' in col ? col.sortable : true }))
    .filter((col) => props.visibleColumns?.[col.key] ?? false));

const preparedItems = computed(() => {
  const sort = unref(internalSortBy);
  const sortDirection = unref(internalSortDirection);
  if (!sort) return props.items;
  const column = props.columns.find((col) => col.key === sort);
  if (!column) {
    console.warn(`Unknown sort key '${sort}' used. Proceeding without sort.`);
    return props.items;
  }

  if (props.disableFrontendSorting) return props.items;

  return Array.from(props.items).sort(
    column.comparator ??
      ((aOb, bOb) => {
        const a = getObjectProperty(aOb, sort);
        const b = getObjectProperty(bOb, sort);
        const aString = typeof a === 'string';
        const bString = typeof b === 'string';
        const aNumber = typeof a === 'number';
        const bNumber = typeof b === 'number';
        let compared = 0;
        if (a && b && (aString || bString)) compared = a.toString().localeCompare(b.toString());
        else if (aNumber && bNumber) compared = a - b;
        else if (!aString && !bString && !aNumber && !bNumber) {
          console.warn('Uncomparable types detected');
        } else compared = a - b;
        return compared * (sortDirection === 'ASC' ? 1 : -1);
      }),
  );
});

const checkedList = useVModel(props, 'checkedIdsList', emit);
const checkItem = (id: any) => {
  if (checkedList.value?.includes(id)) {
    checkedList.value = checkedList.value?.filter((i) => i !== id);
  } else {
    checkedList.value?.push(id);
  }
};
const checkAll = () => {
  if (checkedList.value?.length === preparedItems.value.length) {
    checkedList.value = [];
  } else {
    checkedList.value = [...preparedItems.value.map((i) => i.id)];
  }
};
const isChecked = (id: any) => checkedList.value?.includes(id);
const isAllChecked = computed(
  () => checkedList.value?.length === preparedItems.value.length && preparedItems.value.length > 0,
);
const clearChecked = () => {
  const newList: any[] = [];
  checkedList.value?.forEach((item) => {
    if (preparedItems.value.filter((el) => el.id === item).length > 0) {
      newList.push(item);
    }
  });
  checkedList.value = newList;
};

if (props.choosable) {
  watch(preparedItems, (newVal) => {
    if (!props.busy || newVal.length > 0) {
      clearChecked();
    }
  });
}
</script>

<style lang="scss" scoped>
.data-grid {
  @apply max-h-full max-w-full;
  display: grid;
  grid-template-columns: v-bind(
    "`${choosable ? '2rem ' : ''}${pinnable ? '2rem ' : ''}repeat(${preparedColumns.length}, 1fr)${actions ? ' auto' : ''}`"
  );

  .full-row {
    @apply flex items-center justify-center;
    @apply p-4;
    grid-column: 1 / -1;
  }

  .header-row > * {
    @apply flex items-center;
    @apply bg-table-head-gray;

    &.sortable {
      cursor: pointer;
    }
  }
  .data-row > * {
    @apply bg-background-light;
    @apply flex items-center;
  }
  :deep(.data-row):nth-child(2n + 1) > * {
    @apply bg-table-head-gray;
  }
}
.spinner {
  @apply flex items-center justify-center;
  @apply border border-element-neutral rounded-8;
  @apply p-10 bg-white absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2;
}

.data-cell {
  & > * {
    @apply p-2.5;
  }
}

:deep(.column-link) {
  @apply text-primary;
  text-decoration: underline;
}

.linked-row:hover .row-link,
.linked-row:hover .row-click {
  @apply bg-info-blue-10;
  @apply text-info-blue;
}

.row-link,
.row-click,
.row-click {
  @apply flex items-center;
  @apply w-full h-full;
  transition: none;
}

.row-click {
  @apply cursor-pointer;
}

.action-column {
  position: absolute;
  right: 0;
}

.action-cell {
  @apply sticky right-0 px-4;
}

.checkbox-container {
  cursor: pointer;

  .checkbox {
    border: 2px solid var(--color-element-neutral-100);
    border-radius: 4px;
    background-color: white;
    width: 1.2rem;
    height: 1.2rem;
    margin-left: auto;
    margin-right: auto;

    &.active {
      background-color: var(--color-primary);
    }
  }
}

.pin {
  @apply w-5 h-5;
  margin-left: auto;
  margin-right: auto;
}
</style>
